import React, { useState } from "react";
import axios from "axios";
import PageHero from "../components/PageHero";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { HOSPITAL } from "../data/site";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  "General Inquiry",
  "OPD Appointment",
  "TPA / Insurance",
  "Admission / IPD",
  "Lab / Radiology",
  "Feedback",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Our team will get back to you within a few hours.");
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title={<>We'd love to <span className="italic text-brand-maroon">hear from you.</span></>}
        subtitle="Drop us a message, call our front desk, or visit us directly. We respond to enquiries within a few hours."
      />

      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20 grid md:grid-cols-12 gap-10">
        {/* Form */}
        <div className="md:col-span-7 order-2 md:order-1">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="rounded-3xl bg-white border border-brand-beige p-7 md:p-10"
          >
            <h2 className="font-serif-display text-2xl md:text-3xl text-brand-ink">Send a message</h2>
            <p className="text-sm text-brand-ink/60 mt-1">All fields marked * are required.</p>

            <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name">Full name *</Label>
                <Input
                  id="name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Your name"
                  className="mt-2 rounded-xl bg-brand-cream border-brand-beige focus-visible:ring-brand-maroon"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={onChange("phone")}
                  placeholder="10-digit mobile number"
                  className="mt-2 rounded-xl bg-brand-cream border-brand-beige focus-visible:ring-brand-maroon"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="you@example.com"
                  className="mt-2 rounded-xl bg-brand-cream border-brand-beige focus-visible:ring-brand-maroon"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="subject">Subject</Label>
                <select
                  id="subject"
                  data-testid="contact-subject-select"
                  value={form.subject}
                  onChange={onChange("subject")}
                  className="mt-2 w-full h-10 rounded-xl bg-brand-cream border border-brand-beige px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-maroon"
                >
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={onChange("message")}
                  rows={5}
                  placeholder="Tell us how we can help…"
                  className="mt-2 rounded-xl bg-brand-cream border-brand-beige focus-visible:ring-brand-maroon"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={submitting}
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-maroon text-white font-semibold px-6 py-3 hover:bg-brand-maroonDark transition disabled:opacity-60"
            >
              <Send size={15} />
              {submitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>

        {/* Info */}
        <aside className="md:col-span-5 order-1 md:order-2">
          <div className="rounded-3xl bg-brand-maroon text-white p-8">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#F8C7CB]">Reach us directly</div>
            <h3 className="font-serif-display text-2xl mt-2">Hospital details</h3>
            <ul className="mt-6 space-y-5 text-sm">
              <li className="flex gap-3">
                <MapPin size={18} className="mt-0.5 text-[#F8C7CB] shrink-0" />
                <span className="leading-relaxed" data-testid="contact-address">{HOSPITAL.address}</span>
              </li>
              <li className="flex gap-3 items-center">
                <Phone size={18} className="text-[#F8C7CB]" />
                <a href={`tel:${HOSPITAL.phoneDial}`} data-testid="contact-phone-link" className="hover:underline">{HOSPITAL.phone}</a>
              </li>
              <li className="flex gap-3 items-center">
                <Mail size={18} className="text-[#F8C7CB]" />
                <a href={`mailto:${HOSPITAL.email}`} data-testid="contact-email-link" className="hover:underline break-all">{HOSPITAL.email}</a>
              </li>
              <li className="flex gap-3 items-center">
                <Clock size={18} className="text-[#F8C7CB]" />
                <span>{HOSPITAL.hoursLabel}</span>
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-3xl overflow-hidden border border-brand-beige bg-white">
            <iframe
              title="Map"
              src={HOSPITAL.mapEmbed}
              className="w-full aspect-[4/3]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </aside>
      </section>
    </div>
  );
}
